import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, IndianRupee, ChevronRight, X, Layers, Upload } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth, can } from '@/hooks/useAuth';
import { RateCardFields, blankRateConfig, hasAnyPrice, type RateConfig } from './RateCardFields';
import { ImportRatesDialog } from './ImportRatesDialog';
import { B2bRateCardFields, blankB2bConfig, hasAnyB2bRate, type B2bConfig } from './B2bRateCardFields';

/**
 * Billing plans — the rate cards everything else is priced from.
 *
 * One place to answer "what is our 3 lakh rate?". Each plan holds a rate card per courier, split
 * B2C / B2B. Assigning a plan to a merchant COPIES these numbers, so editing here never silently
 * reprices somebody already sold on the old figures — and a hand-typed rate on a merchant can never
 * go below the cheapest plan on this screen.
 */
interface Slab {
  id: number;
  name: string;
  billingFrom: number;
  billingTo: number | null;
  notes: string | null;
  isActive: boolean;
  sortOrder: number;
  courierCount: number;
  b2cCount: number;
  b2bCount: number;
  couriers: Array<{ id: number; name: string }>;
}
interface SlabRate {
  id: number; courierId: number; courierName: string; serviceType: 'B2C' | 'B2B';
  fuelChargePercent: number | null; config: any;
}
interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B' }

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function billingLabel(s: Slab) {
  if (!s.billingFrom && !s.billingTo) return 'Any billing';
  if (s.billingTo == null) return `${inr(s.billingFrom)} and above`;
  return `${inr(s.billingFrom)} – ${inr(s.billingTo)}`;
}

export function PricingSlabsPage() {
  const qc = useQueryClient();
  // Read-only is a real grant here, not an afterthought: most people who need this screen need to
  // QUOTE from it, not change it. A button that only ever 403s is worse than no button.
  const { user } = useAuth();
  const canAdd = can(user, 'Billing Plans', 'All', 'add');
  const canEdit = can(user, 'Billing Plans', 'All', 'edit');
  const canDelete = can(user, 'Billing Plans', 'All', 'delete');
  const [openId, setOpenId] = useState<number | null>(null);
  const [editSlab, setEditSlab] = useState<Partial<Slab> | null>(null);

  const { data: slabs, isLoading } = useQuery({
    queryKey: ['pricing-slabs'],
    queryFn: async () => (await api.get<Slab[]>('/pricing-slabs')).data,
  });

  const saveSlab = useMutation({
    mutationFn: async (s: Partial<Slab>) =>
      s.id ? (await api.patch(`/pricing-slabs/${s.id}`, s)).data : (await api.post('/pricing-slabs', s)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pricing-slabs'] }); setEditSlab(null); toast.success('Plan saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the plan'),
  });

  const delSlab = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/pricing-slabs/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pricing-slabs'] }); toast.success('Plan deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete the plan'),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2"><Layers size={20} /> Billing Plans</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            The rate cards we sell from, grouped by the billing a merchant does. Assigning a plan to a merchant
            copies its prices onto that merchant — editing a plan here never changes anyone already on it.
            A hand-typed rate on a merchant can never go below the cheapest plan here.
          </p>
        </div>
        {canAdd && (
        <button onClick={() => setEditSlab({ name: '', billingFrom: 0, billingTo: null, isActive: true, sortOrder: (slabs?.length ?? 0) + 1 })}
          className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
          <Plus size={15} /> New plan
        </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : !slabs?.length ? (
        <div className="border border-slate-200 rounded-xl p-8 text-center">
          <Layers size={28} className="mx-auto text-slate-300" />
          <div className="font-medium text-slate-700 mt-2">No billing plans yet</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Create one for each billing tier you quote — for example "1,00,000 Billing" and "3,00,000 Billing" —
            then add each courier's rates inside it.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {slabs.map((s) => (
            <div key={s.id} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
                <button onClick={() => setOpenId(openId === s.id ? null : s.id)} className="flex items-center gap-2 text-left">
                  <ChevronRight size={16} className={`text-slate-400 transition ${openId === s.id ? 'rotate-90' : ''}`} />
                  <div>
                    <div className="font-semibold text-slate-800 flex items-center gap-2">
                      {s.name}
                      {!s.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Inactive</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><IndianRupee size={11} />{billingLabel(s)}</div>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-slate-500 text-right hidden sm:block">
                    <div>{s.courierCount} courier{s.courierCount === 1 ? '' : 's'}</div>
                    <div className="text-slate-400">{s.b2cCount} B2C · {s.b2bCount} B2B</div>
                  </div>
                  {canEdit && (
                  <button onClick={() => setEditSlab(s)} title="Edit plan"
                    className="p-1.5 border border-slate-200 rounded hover:bg-slate-50"><Pencil size={13} /></button>
                  )}
                  {canDelete && (
                  <button
                    onClick={() => { if (confirm(`Delete "${s.name}"?\n\nMerchants already priced from it keep their rates — only the link is removed.`)) delSlab.mutate(s.id); }}
                    title="Delete plan"
                    className="p-1.5 border border-slate-200 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
              {openId === s.id && <SlabRates slabId={s.id} slabName={s.name} />}
            </div>
          ))}
        </div>
      )}

      {editSlab && (
        <SlabModal slab={editSlab} busy={saveSlab.isPending} onClose={() => setEditSlab(null)} onSave={(v) => saveSlab.mutate(v)} />
      )}
    </div>
  );
}

// ── one plan's rate cards ───────────────────────────────────────────────────────────────────────
/** A card is "filled in" differently per service: B2C wants a price, B2B wants a zone rate. */
function cardHasPrice(e: { serviceType: 'B2C' | 'B2B'; config: any }): boolean {
  return e.serviceType === 'B2B' ? hasAnyB2bRate(e.config) : hasAnyPrice(e.config);
}

function SlabRates({ slabId, slabName }: { slabId: number; slabName: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEditRates = can(user, 'Billing Plans', 'All', 'edit');
  const canDeleteRates = can(user, 'Billing Plans', 'All', 'delete');
  const canExportSheet = can(user, 'Billing Plans', 'All', 'export');
  // `courierIds` plural: one card is normally agreed for several couriers at once, and typing it
  // per courier is how two of them end up a rupee apart.
  const [editing, setEditing] = useState<{ courierIds: number[]; serviceType: 'B2C' | 'B2B'; config: any; fuel: string; rateId?: number } | null>(null);
  const [importing, setImporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['pricing-slab', slabId],
    queryFn: async () => (await api.get(`/pricing-slabs/${slabId}`)).data as { rates: SlabRate[] },
  });
  const { data: couriers } = useQuery({
    queryKey: ['couriers', 'active-all'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data,
  });

  const saveRate = useMutation({
    mutationFn: async (v: { courierIds: number[]; serviceType: string; config: any; fuelChargePercent: number | null }) =>
      (await api.post(`/pricing-slabs/${slabId}/rates/bulk`, v)).data as { saved: number; skipped: number; errors: string[] },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pricing-slab', slabId] });
      qc.invalidateQueries({ queryKey: ['pricing-slabs'] });
      setEditing(null);
      // A courier that was refused is named rather than silently dropped.
      if (r.skipped) toast.warning(`Saved for ${r.saved}. Skipped ${r.skipped}: ${r.errors.slice(0, 2).join('; ')}`);
      else toast.success(r.saved === 1 ? 'Rate card saved' : `Rate card saved for ${r.saved} couriers`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the rate card'),
  });

  const delRate = useMutation({
    mutationFn: async (rateId: number) => (await api.delete(`/pricing-slabs/${slabId}/rates/${rateId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-slab', slabId] });
      qc.invalidateQueries({ queryKey: ['pricing-slabs'] });
      toast.success('Rate card removed');
    },
  });

  const rates = data?.rates ?? [];

  return (
    <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-slate-600">Courier rates in this plan</div>
        <div className="flex gap-2">
          {/* Read-only users get the sheet on its own; an editor reaches it through Upload. */}
          {canExportSheet && !canEditRates && (
            <button onClick={() => setImporting(true)}
              className="px-2.5 py-1.5 border border-slate-300 bg-white rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
              <Upload size={12} /> Rate sheet
            </button>
          )}
          {canEditRates && (<>
          <button onClick={() => setImporting(true)}
            className="px-2.5 py-1.5 border border-slate-300 bg-white rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
            <Upload size={12} /> Upload sheet
          </button>
          <button onClick={() => setEditing({ serviceType: 'B2C', config: blankRateConfig, fuel: '', courierIds: [] })}
            className="px-2.5 py-1.5 border border-slate-300 bg-white rounded-lg text-xs flex items-center gap-1 hover:bg-slate-50">
            <Plus size={12} /> Add courier rates
          </button>
          </>)}
        </div>
      </div>

      {isLoading ? (
        <div className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</div>
      ) : !rates.length ? (
        <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg p-3">
            No rates yet. Add several couriers at once, or upload a filled-in sheet — a plan holds its own
          card for B2C and B2B.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {rates.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{r.courierName}</div>
                <div className="text-[11px] text-slate-500">
                  <span className={`px-1.5 py-0.5 rounded ${r.serviceType === 'B2C' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'}`}>{r.serviceType}</span>
                  {' '}{r.serviceType === 'B2B'
                    ? <>Same zone ₹{r.config?.zoneMatrix?.N1?.N1 ?? r.config?.zoneMatrix?.Central?.Central ?? '—'}/kg · min LR ₹{r.config?.otherCharges?.min_lr_charge?.min ?? '—'}</>
                    : <>City ₹{r.config?.forwardBasic?.city ?? '—'} · Zone ₹{r.config?.forwardBasic?.zone ?? '—'}</>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {canEditRates && (
                <button title="Edit rates"
                  onClick={() => setEditing({
                    courierIds: [r.courierId], serviceType: r.serviceType,
                    config: { ...(r.serviceType === 'B2B' ? blankB2bConfig : blankRateConfig), ...(r.config ?? {}) },
                    fuel: r.fuelChargePercent?.toString() ?? '', rateId: r.id,
                  })}
                  className="p-1 border border-slate-200 rounded hover:bg-slate-50"><Pencil size={12} /></button>
                )}
                {canDeleteRates && (
                <button title="Remove" onClick={() => delRate.mutate(r.id)}
                  className="p-1 border border-slate-200 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {importing && <ImportRatesDialog slabId={slabId} slabName={slabName} onClose={() => setImporting(false)} />}

      {editing && (
        <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800">{editing.rateId ? 'Edit' : 'Add'} courier rate</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Service</label>
                  <select value={editing.serviceType} disabled={!!editing.rateId}
                    onChange={(e) => {
                      const st = e.target.value as 'B2C' | 'B2B';
                      // The two services do not share a card shape, so the blank one is swapped with it.
                      setEditing({ ...editing, serviceType: st, courierIds: [], config: st === 'B2B' ? blankB2bConfig : blankRateConfig });
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50">
                    <option value="B2C">B2C</option>
                    <option value="B2B">B2B</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Fuel charge (%)</label>
                  <input type="number" step="0.01" value={editing.fuel}
                    onChange={(e) => setEditing({ ...editing, fuel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-slate-600">
                    Couriers on this card
                    {editing.courierIds.length > 0 && <span className="text-slate-400"> · {editing.courierIds.length} selected</span>}
                  </label>
                  {!editing.rateId && (
                    <div className="flex gap-2 text-[11px]">
                      <button type="button" className="text-indigo-600 hover:underline"
                        onClick={() => setEditing({ ...editing, courierIds: (couriers ?? []).filter((c) => c.orderType === editing.serviceType).map((c) => c.id) })}>
                        Select all
                      </button>
                      <button type="button" className="text-slate-500 hover:underline"
                        onClick={() => setEditing({ ...editing, courierIds: [] })}>Clear</button>
                    </div>
                  )}
                </div>
                <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-50">
                  {(couriers ?? []).filter((c) => c.orderType === editing.serviceType).map((c) => (
                    <label key={c.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${editing.rateId && !editing.courierIds.includes(c.id) ? 'opacity-40' : ''}`}>
                      <input type="checkbox" className="rounded"
                        checked={editing.courierIds.includes(c.id)}
                        disabled={!!editing.rateId}
                        onChange={() => setEditing({
                          ...editing,
                          courierIds: editing.courierIds.includes(c.id)
                            ? editing.courierIds.filter((x) => x !== c.id)
                            : [...editing.courierIds, c.id],
                        })} />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
                {!editing.rateId && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    The same card is saved for every courier ticked. Editing an existing one changes only that courier.
                  </p>
                )}
              </div>

              {editing.serviceType === 'B2B' ? (
                <B2bRateCardFields cfg={editing.config as B2bConfig} onChange={(config) => setEditing({ ...editing, config })} />
              ) : (
                <RateCardFields cfg={editing.config as RateConfig} onChange={(config) => setEditing({ ...editing, config })} />
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button
                  disabled={editing.courierIds.length === 0 || !cardHasPrice(editing) || saveRate.isPending}
                  onClick={() => saveRate.mutate({
                    courierIds: editing.courierIds, serviceType: editing.serviceType,
                    config: editing.config, fuelChargePercent: editing.fuel ? Number(editing.fuel) : null,
                  })}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                  {saveRate.isPending && <Loader2 size={14} className="animate-spin" />}
                  {editing.courierIds.length > 1 ? `Save for ${editing.courierIds.length} couriers` : 'Save rate card'}
                </button>
              </div>
              {!cardHasPrice(editing) && (
                <div className="text-[11px] text-slate-500">
                  {editing.serviceType === 'B2B'
                    ? 'Enter at least one zone rate before saving — the charges alone do not price a shipment.'
                    : 'Enter at least one price above ₹1 before saving.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── create / edit the plan itself ───────────────────────────────────────────────────────────────
function SlabModal({ slab, busy, onClose, onSave }: {
  slab: Partial<Slab>; busy: boolean; onClose: () => void; onSave: (v: Partial<Slab>) => void;
}) {
  const [v, setV] = useState<Partial<Slab>>(slab);
  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{slab.id ? 'Edit plan' : 'New billing plan'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Plan name</label>
            <input value={v.name ?? ''} onChange={(e) => setV({ ...v, name: e.target.value })}
              placeholder="e.g. 3,00,000 Billing" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Billing from (₹)</label>
              <input type="number" value={v.billingFrom ?? 0} onChange={(e) => setV({ ...v, billingFrom: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Billing to (₹)</label>
              <input type="number" value={v.billingTo ?? ''} placeholder="leave blank for “and above”"
                onChange={(e) => setV({ ...v, billingTo: e.target.value === '' ? null : Number(e.target.value) })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (internal)</label>
            <textarea value={v.notes ?? ''} onChange={(e) => setV({ ...v, notes: e.target.value })} rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={v.isActive !== false} onChange={(e) => setV({ ...v, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-xs">Order</span>
              <input type="number" value={v.sortOrder ?? 0} onChange={(e) => setV({ ...v, sortOrder: Number(e.target.value) })}
                className="w-16 px-2 py-1 border border-slate-300 rounded text-sm" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            An inactive plan cannot be chosen on a merchant, and stops counting towards the minimum a custom rate
            has to clear.
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button disabled={!String(v.name ?? '').trim() || busy} onClick={() => onSave(v)}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
